import {
  SEVERITY_ORDER,
  SEVERITY_RANK,
  SEVERITY_SCORE,
  SEVERITY_COLORS,
  buildBrand,
  alpha,
  formatDate,
  getGrade,
  getGradeColor,
  extractSeverity,
  cleanReportDetails,
  extractTitle,
  markdownToBlocks,
  slugify,
} from '../../pentest/reports/externalReportPdfHelpers';

export {
  SEVERITY_ORDER,
  SEVERITY_RANK,
  SEVERITY_SCORE,
  SEVERITY_COLORS,
  alpha,
};

export const VISHING_METHODOLOGY_PARAGRAPHS = [
  'We begin each vishing assessment by confirming the approved scope, defining the target population, and selecting the pretexts that best reflect the operational risks faced by the organization. The assessment team reviews campaign objectives, employee roles, and acceptable testing boundaries so the engagement measures realistic voice-based social engineering exposure without exceeding the agreed rules of engagement.',
  'Once the campaign design is set, we prepare the call workflows, AI voice selections, and scenario logic that will be used during execution. Calls are structured to simulate realistic attacker behavior while maintaining safety controls, consistent scripting boundaries, and repeatable measurement criteria across the target group.',
  'During execution, we capture call outcomes, employee responses, and indicators of whether the pretext succeeded, failed, or was interrupted. The assessment team reviews those outcomes to identify which scenarios were most effective, which behaviors increased risk, and where controls such as verification procedures or escalation paths broke down.',
  'After the calls are completed, we analyze the results, calculate the engagement metrics, and translate those observations into reporting for leadership and technical stakeholders. The goal is to provide a defensible picture of user susceptibility, campaign performance, and the training or procedural improvements that should be prioritized next.',
];

const gradeMessages = {
  'A+': 'Outstanding resilience. Minimal susceptibility was observed.',
  'A-': 'Strong performance with only minor opportunities for improvement.',
  B: 'Good overall, but targeted training would improve consistency.',
  C: 'Fair performance. Multiple users or scenarios showed avoidable susceptibility.',
  D: 'Weak performance. Several control gaps were observed during call execution.',
  F: 'Critical risk. Immediate awareness and process improvements are recommended.',
};

const safeJsonParse = (value, fallback) => {
  try {
    if (value == null) return fallback;
    if (typeof value === 'string') return JSON.parse(value);
    return value;
  } catch {
    return fallback;
  }
};

const toDisplay = (value) => {
  if (value == null || value === '') return 'N/A';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'N/A';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const normalizeStatus = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'passed' || normalized === 'false') return 'Passed';
  if (normalized === 'failed' || normalized === 'true') return 'Failed';
  if (normalized === 'ongoing') return 'Ongoing';
  return value ? String(value) : 'Unknown';
};

const statusToSeverity = (status) => {
  const normalized = normalizeStatus(status);
  if (normalized === 'Failed') return 'High';
  if (normalized === 'Passed') return 'Low';
  if (normalized === 'Ongoing') return 'Medium';
  return 'Info';
};

const buildResultsMarkdown = (user) => {
  const sections = [];

  const summaryFields = [
    ['Phone', user?.phone],
    ['Scenario', user?.scenario],
    ['Voice', user?.voice],
    ['Outcome', normalizeStatus(user?.passOrFail)],
  ].filter(([, value]) => value != null && value !== '');

  if (summaryFields.length) {
    sections.push('## Call Summary');
    summaryFields.forEach(([label, value]) => {
      sections.push(`- **${label}:** ${toDisplay(value)}`);
    });
  }

  const parsedResults = safeJsonParse(user?.results, {});
  if (parsedResults && typeof parsedResults === 'object' && !Array.isArray(parsedResults) && Object.keys(parsedResults).length) {
    sections.push('');
    sections.push('## Call Results');
    Object.entries(parsedResults).forEach(([key, value]) => {
      const label = String(key)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (char) => char.toUpperCase());
      sections.push(`- **${label}:** ${toDisplay(value)}`);
    });
  }

  return sections.join('\n').trim();
};

const buildScenarioStats = (users = []) => {
  const byScenario = new Map();

  users.forEach((user) => {
    const name = user?.scenario ? String(user.scenario) : 'Unknown Scenario';
    const status = normalizeStatus(user?.passOrFail);

    if (!byScenario.has(name)) {
      byScenario.set(name, { name, total: 0, passed: 0, failed: 0, ongoing: 0 });
    }

    const scenario = byScenario.get(name);
    scenario.total += 1;
    if (status === 'Passed') scenario.passed += 1;
    else if (status === 'Failed') scenario.failed += 1;
    else scenario.ongoing += 1;
  });

  return Array.from(byScenario.values())
    .map((scenario) => ({
      ...scenario,
      rate: scenario.total ? Math.round((scenario.failed / scenario.total) * 100) : 0,
    }))
    .sort((left, right) => {
      const rateDelta = (right.rate || 0) - (left.rate || 0);
      if (rateDelta !== 0) return rateDelta;
      return (right.total || 0) - (left.total || 0);
    });
};

const buildVoiceStats = (users = []) => {
  const byVoice = new Map();

  users.forEach((user) => {
    const name = user?.voice ? String(user.voice) : 'Unknown Voice';
    if (!byVoice.has(name)) {
      byVoice.set(name, { name, total: 0, failed: 0 });
    }

    const voice = byVoice.get(name);
    voice.total += 1;
    if (normalizeStatus(user?.passOrFail) === 'Failed') {
      voice.failed += 1;
    }
  });

  return Array.from(byVoice.values())
    .map((voice) => ({
      ...voice,
      failRate: voice.total ? Math.round((voice.failed / voice.total) * 100) : 0,
    }))
    .sort((left, right) => {
      const totalDelta = (right.total || 0) - (left.total || 0);
      if (totalDelta !== 0) return totalDelta;
      return (right.failRate || 0) - (left.failRate || 0);
    });
};

export const buildVishingReportPdfData = ({
  pentestId,
  primaryColor,
  companyName,
  clientName,
  stats,
  exploits,
  executiveSummary,
}) => {
  const brand = buildBrand(primaryColor || '#0A68FF');
  const users = Array.isArray(stats?.users) ? stats.users : [];

  const findings = (exploits || [])
    .filter((item) => item && (item.name || item.report_details))
    .map((item, index) => {
      const cleanedDetails = cleanReportDetails(item.report_details || '');
      const severity = extractSeverity(item);
      const title = extractTitle(cleanedDetails || item.report_details || '', item.name || `Finding ${index + 1}`);

      return {
        ...item,
        id: `${slugify(title || item.name || `finding-${index + 1}`)}-${index + 1}`,
        title,
        severity,
        cleanedDetails,
        markdownBlocks: markdownToBlocks(cleanedDetails || 'No additional details provided.'),
      };
    })
    .sort((left, right) => (SEVERITY_RANK[left.severity] ?? 999) - (SEVERITY_RANK[right.severity] ?? 999));

  const resultFindings = users
    .filter((user) => typeof user?.results === 'string' && user.results.trim() !== '' && user.results !== '{}')
    .map((user, index) => {
      const title = user?.name || user?.phone || `User ${index + 1}`;
      const cleanedDetails = buildResultsMarkdown(user);
      const status = normalizeStatus(user?.passOrFail);

      return {
        ...user,
        id: `${slugify(title)}-${index + 1}`,
        title,
        severity: statusToSeverity(status),
        status,
        cleanedDetails,
        markdownBlocks: markdownToBlocks(cleanedDetails || 'No call results provided.'),
      };
    });

  const resolvedFindings = findings.length ? findings : resultFindings;

  const riskScore = stats?.risk_score ?? null;
  const grade = getGrade(riskScore);
  const gradeColor = getGradeColor(grade);

  const totalUsers = stats?.total_users ?? users.length;
  const totalCalls = stats?.total_calls ?? totalUsers;
  const endedCalls = stats?.ended_calls ?? 0;
  const ongoingCalls = stats?.ongoing_calls ?? Math.max(totalCalls - endedCalls, 0);
  const noAnswerCalls = stats?.no_answer_calls ?? 0;
  const passedUsers = stats?.passed_users ?? users.filter((user) => normalizeStatus(user?.passOrFail) === 'Passed').length;
  const failedUsers = stats?.failed_users ?? users.filter((user) => normalizeStatus(user?.passOrFail) === 'Failed').length;
  const failureRate = totalUsers ? Math.round((failedUsers / totalUsers) * 100) : 0;

  const scenarioStats = buildScenarioStats(users);
  const voiceStats = buildVoiceStats(users);
  const topScenarios = scenarioStats.slice(0, 5);
  const topVoices = voiceStats.slice(0, 5);

  const outcomeStats = [
    { label: 'Passed', value: passedUsers, color: '#2e7d32' },
    { label: 'Failed', value: failedUsers, color: '#c62828' },
    { label: 'Ongoing', value: ongoingCalls, color: '#ef6c00' },
    { label: 'No Answer', value: noAnswerCalls, color: '#64748b' },
  ];

  const summaryMarkdown = String(executiveSummary || '').trim();
  const executiveSummaryMarkdown = summaryMarkdown || [
    `The vishing simulation engaged ${totalCalls} call attempt(s) across ${totalUsers} targeted user(s).`,
    `${failedUsers} user(s) failed the simulation while ${passedUsers} user(s) handled the call appropriately, resulting in an observed failure rate of ${failureRate}%.`,
    'The assessment team recommends targeted follow-up training for the scenarios and user behaviors that produced the highest failure rates during the campaign.',
  ].join('\n\n');

  const tocItems = [
    { id: 'cover', title: 'Cover', level: 1 },
    { id: 'confidentiality', title: 'Confidentiality', level: 1 },
    { id: 'table-of-contents', title: 'Table of Contents', level: 1 },
    { id: 'executive-security-assessment', title: 'Executive Security Assessment', level: 1 },
    { id: 'executive-summary', title: 'Executive Summary', level: 1 },
    { id: 'methodology', title: 'Methodology', level: 1 },
    { id: 'findings', title: 'Findings', level: 1 },
    ...resolvedFindings.map((finding) => ({
      id: finding.id,
      title: finding.title,
      level: 2,
    })),
  ];

  return {
    pentestId,
    clientName: clientName || stats?.meta?.client_name || stats?.client || 'Client Name',
    companyName: companyName || 'StealthNet AI',
    reportDate: formatDate(stats?.meta?.generated_at || Date.now()),
    testingType: 'Vishing (Voice Phishing) Simulation',
    organizationName: stats?.meta?.client_name || stats?.client || clientName || 'Client',
    brand,
    grade,
    gradeColor,
    gradeMessage: gradeMessages[grade] || '',
    riskScore,
    totalUsers,
    totalCalls,
    endedCalls,
    ongoingCalls,
    noAnswerCalls,
    passedUsers,
    failedUsers,
    failureRate,
    activeScenarioCount: scenarioStats.length,
    activeVoiceCount: voiceStats.length,
    outcomeStats,
    topScenarios,
    topVoices,
    executiveSummaryMarkdown,
    executiveSummaryBlocks: markdownToBlocks(executiveSummaryMarkdown),
    executiveSummary: executiveSummaryMarkdown
      .split(/\n{2,}/g)
      .map((paragraph) => paragraph.trim())
      .filter(Boolean),
    methodology: VISHING_METHODOLOGY_PARAGRAPHS,
    findings: resolvedFindings,
    tocItems,
  };
};
